import useProjectDomains from "../../../../api/utilities/useProjectDomains.js";
import Spinner from "../../../../components/Spinner.jsx";
import Creatable from "react-select/creatable";
import {useEffect, useMemo, useState} from "react";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {ProjectApi} from "../../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../../api/utilities/useAuthorityConfiguration.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrashCan} from "@fortawesome/free-regular-svg-icons";
import {useSupportedCurrencies, useSupportedCurrency} from "../../../../api/utilities/useSupportedCurrencies.js";
import Select from "react-select";
import {useTranslation} from "react-i18next";
import {IntegrationAddButton} from "../IntegrationDetailUi.jsx";
import StoredSecretInput from "./StoredSecretInput.jsx";

export default function HostNameAndTldAndCurrencyCodeAndApiKeyList({
                                                                       project,
                                                                       projectIntegration,
                                                                       value,
                                                                       onChange,
                                                                   apiKeySameForAll
                                                               }) {
    let authorityConfiguration = useAuthorityConfiguration();
    let {t} = useTranslation();

    apiKeySameForAll = apiKeySameForAll ?? false;

    let projectDomains = useProjectDomains(project);
    let [projectDomainNameWithTldAndApiKeyList, setProjectDomainNameWithTldAndApiKeyList] = useState(null);

    let supportedCurrencies = useSupportedCurrencies();
    let supportedCurrency = useSupportedCurrency();
    let rows = value ?? [];
    let defaultRow = useMemo(() => ({
        hostName: '',
        tld: '',
        currencyCode: project.currencyCode || 'CZK',
        apiKey: '',
    }), [project.currencyCode]);

    let updateRow = (index, updatedRow) => {
        onChange(rows.map((row, checkedIndex) => checkedIndex === index ? updatedRow : row));
    };

    useEffect(() => {
        if (projectIntegration === null) {
            setProjectDomainNameWithTldAndApiKeyList([defaultRow]);
            return;
        }

        new ProjectApi(authorityConfiguration)
            .hostNameAndTLDAndApiKey({
                integrationId: projectIntegration.id
            })
            .then((list) => setProjectDomainNameWithTldAndApiKeyList(
                list.length ? list.map((row) => ({...row, apiKey: ''})) : [defaultRow]
            ))
            .catch(() => setProjectDomainNameWithTldAndApiKeyList([defaultRow]));
    }, [project.id, projectIntegration?.id, defaultRow]);

    let hostNameOptions = useMemo(() => {
        if (projectDomains === null) {
            return [];
        }
        return projectDomains.map((projectDomain) => ({
            value: projectDomain.projectDomain.hostName,
            label: projectDomain.projectDomain.hostName
        }));
    }, [projectDomains]);
    let glamiCountryOptions = useMemo(() => [
        {value: 'cz', label: t('project.integration-glami-country-cz')},
        {value: 'sk', label: t('project.integration-glami-country-sk')},
        {value: 'pl', label: t('project.integration-glami-country-pl')},
        {value: 'de', label: t('project.integration-glami-country-de')},
        {value: 'it', label: t('project.integration-glami-country-it')},
        {value: 'ro', label: t('project.integration-glami-country-ro')},
        {value: 'hu', label: t('project.integration-glami-country-hu')},
        {value: 'gr', label: t('project.integration-glami-country-gr')},
        {value: 'bg', label: t('project.integration-glami-country-bg')},
        {value: 'hr', label: t('project.integration-glami-country-hr')},
        {value: 'si', label: t('project.integration-glami-country-si')},
        {value: 'eco', label: t('project.integration-glami-country-eco')},
        {value: 'ee', label: t('project.integration-glami-country-ee')},
        {value: 'lt', label: t('project.integration-glami-country-lt')},
        {value: 'lv', label: t('project.integration-glami-country-lv')},
    ], [t]);

    useEffect(() => {
        if (projectDomainNameWithTldAndApiKeyList !== null) {
            onChange(projectDomainNameWithTldAndApiKeyList);
        }
    }, [projectDomainNameWithTldAndApiKeyList]);

    return <LoadingComponent loading={projectDomainNameWithTldAndApiKeyList === null}>
        {projectDomains === null && <Spinner/>}
        {projectDomains && <div className={'flex flex-col gap-3'}>
            {rows.map((row, index) => {
                return <div className={'rounded-xl border border-[#ddd] bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40'} key={'glami-settings-row-' + index}>
                    <div className={'flex items-start justify-between gap-3'}>
                        <div className={'text-sm font-semibold text-slate-700'}>
                            {t('project.integration-domain-and-api-key-number', {number: index + 1})}
                        </div>
                        <button
                            type={'button'}
                            className={'flex h-8 w-8 items-center justify-center rounded-md border border-red-500 bg-red-500 text-sm text-white shadow-sm hover:border-red-600 hover:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2'}
                            title={t('project.integration-remove-domain')}
                            onClick={() => {
                                let newRows = rows.filter((x, checkedIdx) => index !== checkedIdx);
                                onChange(newRows.length ? newRows : [defaultRow]);
                            }}
                        >
                            <FontAwesomeIcon icon={faTrashCan}/>
                        </button>
                    </div>

                    <div className={'mt-3 flex flex-col gap-4'}>
                        <div className={'flex flex-col gap-1.5'}>
                            <label className={'text-sm font-medium text-slate-700'}>
                                {t('project.integration-shop-domain')}
                            </label>
                            <Creatable
                                classNamePrefix={'select'}
                                value={row.hostName ? {label: row.hostName, value: row.hostName} : null}
                                onChange={(e) => {
                                    updateRow(index, {...row, hostName: e?.value ?? ''});
                                }}
                                options={hostNameOptions}
                                placeholder={t('project.integration-shop-domain-placeholder')}
                                noOptionsMessage={() => t('project.integration-no-options')}
                            />
                        </div>

                        <div className={'grid gap-4 md:grid-cols-[minmax(180px,1fr)_minmax(220px,1fr)]'}>
                            <div className={'flex flex-col gap-1.5'}>
                                <label className={'text-sm font-medium text-slate-700'}>
                                    {t('project.integration-glami-country')}
                                </label>
                                <Select
                                    classNamePrefix={'select'}
                                    value={
                                        glamiCountryOptions.find((option) => option.value === row.tld)
                                        ?? (row.tld ? {value: row.tld, label: `glami.${row.tld}`} : null)
                                    }
                                    onChange={(option) => {
                                        updateRow(index, {...row, tld: option?.value ?? ''});
                                    }}
                                    options={glamiCountryOptions}
                                    placeholder={t('project.integration-glami-country-placeholder')}
                                    noOptionsMessage={() => t('project.integration-no-options')}
                                />
                            </div>

                            <div className={'flex flex-col gap-1.5'}>
                                <label className={'text-sm font-medium text-slate-700'}>
                                    {t('project.integration-currency')}
                                </label>
                                <Select
                                    classNamePrefix={'select'}
                                    value={supportedCurrency(row.currencyCode)}
                                    onChange={(e) => {
                                        updateRow(index, {...row, currencyCode: e.value});
                                    }}
                                    options={supportedCurrencies}
                                    noOptionsMessage={() => t('project.integration-no-options')}
                                />
                            </div>
                        </div>

                        <div className={'flex flex-col gap-1.5'}>
                            <label className={'text-sm font-medium text-slate-700'}>
                                {t('project.integration-glami-api-key')}
                            </label>
                            <StoredSecretInput
                                configured={row.apiKeyConfigured ?? false}
                                value={row.apiKey ?? ''}
                                placeholder={t('project.integration-api-key-placeholder')}
                                onChange={(apiKey) => {
                                    onChange(rows.map((row, checkedIndex) => {
                                        if (index === checkedIndex || apiKeySameForAll) {
                                            return {...row, apiKey};
                                        }

                                        return row;
                                    }));
                                }}
                            />
                        </div>
                    </div>
                </div>;
            })}
        </div>}
        <div className={'flex justify-center pt-3'}>
            <IntegrationAddButton onClick={() => onChange([...rows, defaultRow])}>
                Přidat doménu
            </IntegrationAddButton>
        </div>
    </LoadingComponent>;
}
